<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-1">{{ $t('auth.setPassword.title') }}</h4>
              <p class="mb-3">{{ $t('auth.setPassword.subtitle') }}</p>
            </div>
            
            <!-- Loading state -->
            <div v-if="isValidatingToken" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('auth.setPassword.loading') }}</span>
              </div>
              <p class="mt-2 text-muted">{{ $t('auth.setPassword.loading') }}</p>
            </div>
            
            <!-- Error state -->
            <div v-else-if="tokenError" class="text-center py-4">
              <div class="alert alert-danger" role="alert">
                <i class="ph-duotone ph-warning-circle me-2"></i>
                {{ tokenError }}
              </div>
              <p class="text-muted">{{ $t('auth.setPassword.contactSupport') }}</p>
              <div class="d-grid mt-4">
                <router-link to="/login" class="btn btn-outline-primary">
                  {{ $t('auth.setPassword.backToLogin') }} {{ $t('auth.setPassword.login') }}
                </router-link>
              </div>
            </div>
            
            <!-- Password setup form -->
            <div v-else-if="userEmail && !successMessage">
              <!-- User email display -->
              <div class="alert alert-light border mb-4" role="alert">
                <i class="ph-duotone ph-user me-2 text-primary"></i>
                {{ $t('auth.setPassword.userEmail') }} <strong>{{ userEmail }}</strong>
              </div>
              
              <!-- Form errors -->
              <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
                <i class="ph-duotone ph-warning-circle me-2"></i>
                {{ error }}
                <button type="button" class="btn-close" @click="error = null"></button>
              </div>
              
              <!-- Password field -->
              <div class="mb-3">
                <label class="form-label text-muted">{{ $t('auth.setPassword.password') }}</label>
                <div class="position-relative">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control pe-5"
                    :class="{ 'is-invalid': v$.password.$error }"
                    :placeholder="$t('auth.setPassword.password')"
                    v-model="password"
                    @blur="v$.password.$touch"
                  />
                  <button 
                    type="button" 
                    class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                    @click="showPassword = !showPassword"
                    style="z-index: 10; border: none; background: none;"
                  >
                    <i :class="showPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                  </button>
                </div>
                <div v-if="v$.password.$error" class="text-danger text-start small mt-1">
                  {{ v$.password.$errors[0].$message }}
                </div>
                <!-- Password strength indicator -->
                <div v-if="password" class="mt-2">
                  <div class="progress" style="height: 6px;">
                    <div 
                      class="progress-bar" 
                      :class="passwordStrengthClass"
                      :style="{ width: passwordStrengthPercent + '%' }"
                    ></div>
                  </div>
                  <small :class="passwordStrengthTextClass">
                    {{ passwordStrengthText }}
                  </small>
                  <!-- zxcvbn feedback for weak passwords -->
                  <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.warning" class="mt-1">
                    <small class="text-warning">
                      <i class="ph-duotone ph-warning me-1"></i>
                      {{ passwordStrength.feedback.warning }}
                    </small>
                  </div>
                  <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.suggestions.length > 0" class="mt-1">
                    <small class="text-muted">
                      <i class="ph-duotone ph-lightbulb me-1"></i>
                      {{ passwordStrength.feedback.suggestions[0] }}
                    </small>
                  </div>
                </div>
              </div>
              
              <!-- Confirm password field -->
              <div class="mb-4">
                <label class="form-label text-muted">{{ $t('auth.setPassword.confirmPassword') }}</label>
                <div class="position-relative">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control pe-5"
                    :class="{ 'is-invalid': v$.confirmPassword.$error }"
                    :placeholder="$t('auth.setPassword.confirmPassword')"
                    v-model="confirmPassword"
                    @blur="v$.confirmPassword.$touch"
                  />
                  <button 
                    type="button" 
                    class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                    @click="showConfirmPassword = !showConfirmPassword"
                    style="z-index: 10; border: none; background: none;"
                  >
                    <i :class="showConfirmPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                  </button>
                </div>
                <div v-if="v$.confirmPassword.$error" class="text-danger text-start small mt-1">
                  {{ v$.confirmPassword.$errors[0].$message }}
                </div>
              </div>
              
              <!-- Submit button -->
              <div class="d-grid">
                <button 
                  type="button" 
                  class="btn btn-primary btn-lg" 
                  @click="setPassword" 
                  :disabled="isLoading || !isFormValid"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('auth.setPassword.setButton') }}
                </button>
              </div>
            </div>
            
            <!-- Success state -->
            <div v-else-if="successMessage" class="text-center py-4">
              <div class="alert alert-success" role="alert">
                <i class="ph-duotone ph-check-circle me-2"></i>
                {{ successMessage }}
              </div>
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">{{ $t('auth.setPassword.redirecting') }}</span>
              </div>
              <p class="mt-2 text-muted">{{ $t('auth.setPassword.redirectCountdown', { seconds: countdown }) }}</p>
            </div>
            
            <!-- Back to login link -->
            <div v-if="!isValidatingToken && !successMessage" class="text-center mt-4">
              <p class="mb-0 text-muted">
                {{ $t('auth.setPassword.backToLogin') }}
                <router-link to="/login" class="link-primary ms-1">{{ $t('auth.setPassword.login') }}</router-link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, minLength, sameAs } from '@vuelidate/validators';
import authService from '@/services/authService';
import passwordStrengthService from '@/services/passwordStrengthService';
import AuthFooter from '@/components/auth/AuthFooter.vue';

export default {
  components: {
    AuthFooter
  },
  name: 'SetPassword',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    
    // Reactive data
    const token = ref('');
    const userEmail = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const error = ref(null);
    const tokenError = ref(null);
    const successMessage = ref(null);
    const isValidatingToken = ref(false);
    const isLoading = ref(false);
    const countdown = ref(3);
    
    // Validation rules
    const rules = {
      password: { 
        required: required,
        minLength: minLength(6)
      },
      confirmPassword: {
        required: required,
        sameAsPassword: sameAs(password)
      }
    };

    const v$ = useVuelidate(rules, { password, confirmPassword });

    // Computed properties
    const isFormValid = computed(() => {
      return password.value.length >= 6 && 
             password.value === confirmPassword.value &&
             passwordStrengthService.meetsMinimumStrength(passwordStrength.value.score, 2);
    });

    // Advanced password strength calculation using zxcvbn-ts
    const passwordStrength = computed(() => {
      const pwd = password.value;
      if (!pwd) {
        return {
          score: 0,
          strength: 'veryWeak',
          text: '',
          class: 'bg-secondary',
          textClass: 'text-muted',
          percentage: 0,
          feedback: { warning: '', suggestions: [] }
        };
      }
      
      // Prepare user inputs to penalize personal info in passwords
      const userInputs = [];
      if (userEmail.value) {
        const emailParts = userEmail.value.split('@');
        userInputs.push(emailParts[0]); // username part
        userInputs.push(...emailParts[0].split('.')); // split by dots
      }
      
      // Add common site-specific terms
      userInputs.push('nozzle', 'supplier', 'portal', 'password');
      
      // Evaluate password using zxcvbn-ts
      const evaluation = passwordStrengthService.evaluatePassword(pwd, userInputs);
      
      return {
        score: evaluation.score,
        strength: evaluation.strength,
        text: passwordStrengthService.getLocalizedStrength(evaluation.strength, t),
        class: passwordStrengthService.getStrengthClass(evaluation.score),
        textClass: passwordStrengthService.getStrengthTextClass(evaluation.score),
        percentage: passwordStrengthService.getStrengthPercentage(evaluation.score),
        feedback: evaluation.feedback,
        guesses: evaluation.guesses,
        crackTime: evaluation.crackTimesDisplay,
        calcTime: evaluation.calculation_time
      };
    });

    const passwordStrengthPercent = computed(() => passwordStrength.value.percentage);
    const passwordStrengthClass = computed(() => passwordStrength.value.class);
    const passwordStrengthText = computed(() => passwordStrength.value.text);
    const passwordStrengthTextClass = computed(() => passwordStrength.value.textClass);

    // Token validation and user setup
    const validateToken = async () => {
      isValidatingToken.value = true;
      tokenError.value = null;

      try {
        // Get token from URL query parameters
        token.value = route.query.token || '';
        
        if (!token.value) {
          tokenError.value = t('auth.setPassword.invalidToken');
          isValidatingToken.value = false;
          return;
        }

        // Validate token with the server
        const response = await authService.validatePasswordSetupToken(token.value);
        
        if (response && response.email) {
          userEmail.value = response.email;
        } else {
          tokenError.value = t('auth.setPassword.invalidToken');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        
        if (err.response?.status === 400 || err.response?.status === 404) {
          tokenError.value = t('auth.setPassword.invalidToken');
        } else {
          tokenError.value = t('auth.setPassword.networkError');
        }
      } finally {
        isValidatingToken.value = false;
      }
    };

    // Countdown and redirect functionality
    let countdownInterval = null;
    
    const startCountdown = () => {
      countdownInterval = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownInterval);
          router.push('/login');
        }
      }, 1000);
    };

    // Set password function
    const setPassword = async () => {
      error.value = null;
      
      // Validate form
      const isFormValid = await v$.value.$validate();
      if (!isFormValid) {
        error.value = t('auth.setPassword.validationError');
        return;
      }
      
      if (!token.value) {
        error.value = t('auth.setPassword.invalidToken');
        return;
      }
      
      isLoading.value = true;
      
      try {
        const passwordData = {
          token: token.value,
          password: password.value,
          confirmPassword: confirmPassword.value
        };
        
        const result = await authService.setPassword(passwordData);
        
        // Check API response format: boolean (true = başarılı)
        if (result === true) {
          successMessage.value = t('auth.setPassword.success');
          
          // Reset form
          password.value = '';
          confirmPassword.value = '';
          v$.value.$reset();
          
          // Start countdown and redirect
          startCountdown();
        } else {
          error.value = t('auth.setPassword.error');
        }
        
      } catch (err) {
        console.error('Set password error:', err);
        
        if (err.response?.status === 400) {
          if (err.response.data?.errorMessage?.includes('token') || err.response.data?.message?.includes('token')) {
            error.value = t('auth.setPassword.invalidToken');
          } else {
            error.value = err.response.data?.errorMessage || err.response.data?.message || t('auth.setPassword.error');
          }
        } else if (err.response?.status === 422) {
          error.value = err.response.data?.errorMessage || err.response.data?.message || t('auth.setPassword.validationError');
        } else {
          error.value = t('auth.setPassword.networkError');
        }
      
        isLoading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      validateToken();
    });

    onUnmounted(() => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    });

    return {
      userEmail,
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      error,
      tokenError,
      successMessage,
      isValidatingToken,
      isLoading,
      countdown,
      setPassword,
      v$,
      isFormValid,
      passwordStrength,
      passwordStrengthPercent,
      passwordStrengthClass,
      passwordStrengthText,
      passwordStrengthTextClass
    };
  }
};
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.progress {
  transition: all 0.3s ease;
  border-radius: 3px;
}

.btn-link {
  text-decoration: none !important;
}

.btn-link:hover {
  color: var(--bs-primary) !important;
}

.position-relative .btn-link {
  padding: 0;
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert {
  border-radius: 0.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-control {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
}

.card {
  border-radius: 1rem;
  border: none;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
}
</style> 